import type { ComponentProps } from 'react';
import { asWidgetProps, SCROLL_TO_ELEMENT_DEFAULT } from 'Src/types/widget-helpers';
import React, { Fragment, useState } from 'react';
import { isEmpty, noop } from 'lodash';
import { classnames } from 'Utils/classnames/classnames.utils';
import { AFTER_APP } from 'Components/frameworks/portal/Portal.constants';
import { PriceRemoteDetailsLayer } from 'Components/widgets/price-remote/price-remote-details-layer/PriceRemoteDetailsLayer';
import { ICONS } from 'Components/utils/icon/icons';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import type { PriceTicketContentProps } from 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import { t } from 'Src/i18n';
import { BlockLink } from 'Components/frameworks/block-link/BlockLink';
import { Button } from 'Components/frameworks/button/Button';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Price } from 'Components/frameworks/price/Price';
import { PriceRemoteDetailsToggle } from 'Components/widgets/price-remote/price-remote-details-toggle/PriceRemoteDetailsToggle';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { Title } from 'Components/frameworks/title/Title';
import { useTracking } from 'Tracking/tracking-api/useTracking';
type Props = PriceTicketContentProps & {
  isLite: boolean;
};
export const PriceTicketNarrow = ({
  allInclusiveServices = [],
  button,
  className,
  hasCallCenter,
  isLite,
  isLoading,
  items = [],
  price,
  subtitle,
  title,
  travelAdvisorConfig,
}: Props) => {
  const [isDetailOpened, setIsDetailOpened] = useState(false);
  const track = useTracking();

  const openClosePriceDetails = () => {
    track({
      actionGA: 'priceBar',
      labelGA: isDetailOpened ? 'closeAllInclusive' : 'openAllInclusive',
    });
    setIsDetailOpened(!isDetailOpened);
  };

  const hasPrice = !hasCallCenter && !isEmpty(price) && price.bestPrice;
  const hasBlockLink = hasCallCenter && !isLite;
  const hasDetail = !isLite && !hasCallCenter && !isEmpty(items);
  const scroll =
    button && button.url && /^(#|\.)/.test(button.url)
      ? () =>
          scrollToElement(
            button.url,
            500,
            `.${NAVIGATION_OFFSET_SELECTOR}`,
            SCROLL_TO_ELEMENT_DEFAULT,
          )
      : noop;
  return (
    <div
      className={classnames(
        {
          'opacity-75': isLoading,
          'animation-duration-750 animate-fadeIn': !isLite,
          'animation-duration-500 animate-slideInBottom': isLite,
        },
        className,
      )}
      data-testid="PriceTicketNarrow"
    >
      <SiteContainer isWide={isLite}>
        <div
          className={classnames('relative isolate rounded-12 bg-white p-10 shadow-diffuse', {
            'border-1 border-pearl': !isLite,
            'border-t-1 border-lightGrey': isLite,
            'flex items-center justify-center': isLoading,
          })}
        >
          {isLoading ? (
            <Spinner className="text-black" size="small" />
          ) : (
            <Fragment>
              {!isLite && (
                <Title
                  className="mb-10 flex-1 flex-col !items-start"
                  dataTestId="priceTicket-title"
                  hLevel={1}
                  theme="title-sans-n1"
                >
                  <span className="text-16 text-sienna" data-testid="priceTicket-subtitle">
                    {subtitle}
                  </span>
                  {title}
                </Title>
              )}
              {hasBlockLink && travelAdvisorConfig && (
                <BlockLink
                  className={classnames('mb-10 text-black', {
                    hidden: isLite,
                  })}
                  icon={ICONS.PHONE_BUBBLE}
                  texts={travelAdvisorConfig.text}
                  url={travelAdvisorConfig.href}
                />
              )}
              {hasPrice && (
                <Price
                  {...price}
                  className={classnames('mb-10', {
                    'justify-center': isLite,
                  })}
                  from={t('price.format.from')}
                  hasInitialPrice={false}
                  hasTotalPrice
                />
              )}

              {hasDetail && (
                <Fragment>
                  <p className="mb-10 text-center font-sans text-14 font-bold text-black">
                    {t('productPriceTicketLine.startingPriceCriteriaLabel', {}, '')}
                  </p>
                  <div className="mb-20 flex flex-1 items-center gap-20">
                    {items.map(({ iconName, label, shortDesc, subDesc }) => (
                      <DynamicLink
                        className="flex items-center gap-10 font-sans text-12 text-black"
                        data-testid="PriceTicketNarrow-item"
                        key={iconName}
                        url={button?.url}
                      >
                        <Icon
                          {...asWidgetProps<ComponentProps<typeof Icon>>({
                            className: 'shrink-0 text-sienna',
                            name: iconName,
                            title: label,
                            width: '1rem',
                          })}
                        />
                        {shortDesc} {subDesc && <div className="font-bold">{subDesc}</div>}
                      </DynamicLink>
                    ))}
                  </div>
                </Fragment>
              )}

              <Button {...button} className="w-full" onClick={scroll} size="small" />

              {!!allInclusiveServices?.length && (
                <Fragment>
                  <div className="mt-10 flex justify-center">
                    <PriceRemoteDetailsToggle
                      handleClick={openClosePriceDetails}
                      isCollapsed={!isDetailOpened}
                    />
                  </div>
                  {isDetailOpened && (
                    <PriceRemoteDetailsLayer
                      allInclusiveServices={allInclusiveServices}
                      closeLabel={t('common.close')}
                      isLoading={isLoading}
                      nameRender={AFTER_APP}
                      onBack={() => setIsDetailOpened(false)}
                    />
                  )}
                </Fragment>
              )}
            </Fragment>
          )}
        </div>
      </SiteContainer>
    </div>
  );
};
